import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Popover,
  Pagination,
} from '@mui/material'
import {
  Favorite,
  FavoriteBorder,
  LocationOn,
  PlaylistAdd,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { LIST_PROJECTS } from '@/graphql/queries'
import { Project, ProjectFilter, Metal, ProjectStage, DatePrecision } from '@/types'
import { AddToWatchlistModal } from './AddToWatchlistModal'

interface ProjectsTableProps {
  filter?: ProjectFilter
  onProjectSelect?: (project: Project) => void
  onToggleFavorite?: (projectId: string, isFavorite: boolean) => void
}

type SortField = 'name' | 'country' | 'primaryMetal' | 'stage' | 'operators'
type SortDirection = 'asc' | 'desc'

// Configurable page size
const PAGE_SIZE = 5

// Number formatter with consistent thousands separator tied to app language
const fmt = (n: number | string, lang: string = 'es') => {
  const num = Number(n)
  if (isNaN(num)) return String(n)
  const isSpanish = lang === 'es'
  const [intPart, decPart] = num.toString().split('.')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, isSpanish ? '.' : ',')
  const decSep = isSpanish ? ',' : '.'
  return decPart !== undefined ? `${intFormatted}${decSep}${decPart}` : intFormatted
}

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  filter,
  onProjectSelect,
  onToggleFavorite,
}) => {
  const { t, i18n } = useTranslation()
  const n = (val: number | string): string => fmt(val, i18n.language)
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [reservesAnchorEl, setReservesAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [inferredResourcesAnchorEl, setInferredResourcesAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [historicalAnchorEl, setHistoricalAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [guidanceAnchorEl, setGuidanceAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [operatorsAnchorEl, setOperatorsAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [aiscHistAnchorEl, setAiscHistAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [aiscGuidanceAnchorEl, setAiscGuidanceAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [cashCostHistAnchorEl, setCashCostHistAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [cashCostGuidanceAnchorEl, setCashCostGuidanceAnchorEl] = useState<Record<string, HTMLElement | null>>({})
  const [watchlistModalOpen, setWatchlistModalOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<{ id: string; name: string } | null>(null)

  // Reset to page 1 when filters or sort change (serialize filter to compare by value, not reference)
  const filterKey = JSON.stringify(filter)
  useEffect(() => { setPage(1) }, [filterKey, sortField, sortDirection])
  const { data, loading, error } = useQuery(LIST_PROJECTS, {
    variables: {
      filter,
      limit: 500,
    },
    errorPolicy: 'all',
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleResourcesPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setResourcesAnchorEl({ ...resourcesAnchorEl, [projectId]: event.currentTarget })
  }

  const handleResourcesPopoverClose = (projectId: string) => {
    setResourcesAnchorEl({ ...resourcesAnchorEl, [projectId]: null })
  }

  const handleInferredResourcesPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setInferredResourcesAnchorEl({ ...inferredResourcesAnchorEl, [projectId]: event.currentTarget })
  }

  const handleInferredResourcesPopoverClose = (projectId: string) => {
    setInferredResourcesAnchorEl({ ...inferredResourcesAnchorEl, [projectId]: null })
  }

  const handleReservesPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setReservesAnchorEl({ ...reservesAnchorEl, [projectId]: event.currentTarget })
  }

  const handleReservesPopoverClose = (projectId: string) => {
    setReservesAnchorEl({ ...reservesAnchorEl, [projectId]: null })
  }

  const handleHistoricalPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setHistoricalAnchorEl({ ...historicalAnchorEl, [projectId]: event.currentTarget })
  }

  const handleHistoricalPopoverClose = (projectId: string) => {
    setHistoricalAnchorEl({ ...historicalAnchorEl, [projectId]: null })
  }

  const handleOpenWatchlistModal = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation()
    setSelectedProject({ id: project.id, name: project.name })
    setWatchlistModalOpen(true)
  }

  const handleCloseWatchlistModal = () => {
    setWatchlistModalOpen(false)
    setSelectedProject(null)
  }

  const handleGuidancePopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setGuidanceAnchorEl({ ...guidanceAnchorEl, [projectId]: event.currentTarget })
  }

  const handleGuidancePopoverClose = (projectId: string) => {
    setGuidanceAnchorEl({ ...guidanceAnchorEl, [projectId]: null })
  }

  const handleOperatorsPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setOperatorsAnchorEl({ ...operatorsAnchorEl, [projectId]: event.currentTarget })
  }

  const handleOperatorsPopoverClose = (projectId: string) => {
    setOperatorsAnchorEl({ ...operatorsAnchorEl, [projectId]: null })
  }

  const handleAiscHistPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setAiscHistAnchorEl({ ...aiscHistAnchorEl, [projectId]: event.currentTarget })
  }
  const handleAiscHistPopoverClose = (projectId: string) => {
    setAiscHistAnchorEl({ ...aiscHistAnchorEl, [projectId]: null })
  }

  const handleAiscGuidancePopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setAiscGuidanceAnchorEl({ ...aiscGuidanceAnchorEl, [projectId]: event.currentTarget })
  }
  const handleAiscGuidancePopoverClose = (projectId: string) => {
    setAiscGuidanceAnchorEl({ ...aiscGuidanceAnchorEl, [projectId]: null })
  }

  const handleCashCostHistPopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setCashCostHistAnchorEl({ ...cashCostHistAnchorEl, [projectId]: event.currentTarget })
  }
  const handleCashCostHistPopoverClose = (projectId: string) => {
    setCashCostHistAnchorEl({ ...cashCostHistAnchorEl, [projectId]: null })
  }

  const handleCashCostGuidancePopoverOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setCashCostGuidanceAnchorEl({ ...cashCostGuidanceAnchorEl, [projectId]: event.currentTarget })
  }
  const handleCashCostGuidancePopoverClose = (projectId: string) => {
    setCashCostGuidanceAnchorEl({ ...cashCostGuidanceAnchorEl, [projectId]: null })
  }

  // Helper function to format metal symbols (CU -> Cu, AU -> Au, etc.)
  const formatMetalSymbol = (metal: Metal): string => {
    const symbol = metal.toString()
    return symbol.charAt(0) + symbol.slice(1).toLowerCase()
  }

  // Helper function to format first production date based on precision
  const formatFirstProduction = (date?: string, precision?: DatePrecision): string => {
    if (!date || !precision) return '-'
    
    const d = new Date(date)
    const year = d.getFullYear()
    const month = d.getMonth() + 1
    
    switch (precision) {
      case 'YEAR':
        return year.toString()
      case 'SEMESTER':
        const semester = month <= 6 ? 1 : 2
        return `H${semester}${year}`
      case 'QUARTER':
        const quarter = Math.ceil(month / 3)
        return `Q${quarter}${year}`
      case 'MONTH':
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
        return `${monthNames[month - 1]} ${year}`
      case 'DAY':
        return `${d.getDate()}/${month}/${year}`
      default:
        return '-'
    }
  }


  const sortedProjects = useMemo(() => {
    if (!data?.listProjects?.items) return []

    const projects = [...data.listProjects.items]
    
    return projects.sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'country':
          aValue = a.country.toLowerCase()
          bValue = b.country.toLowerCase()
          break
        case 'primaryMetal':
          aValue = a.primaryMetal
          bValue = b.primaryMetal
          break
        case 'stage':
          aValue = a.stage
          bValue = b.stage
          break
        case 'operators':
          aValue = a.operators[0]?.name.toLowerCase() || ''
          bValue = b.operators[0]?.name.toLowerCase() || ''
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data?.listProjects?.items, sortField, sortDirection])

  const totalPages = Math.ceil(sortedProjects.length / PAGE_SIZE)
  const paginatedProjects = sortedProjects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const getMetalColor = (metal: Metal) => {
    switch (metal) {
      case Metal.AU:
        return '#FFD700'
      case Metal.AG:
        return '#C0C0C0'
      case Metal.CU:
        return '#B87333'
      case Metal.PGM:
        return '#E5E4E2'
      case Metal.ZN:
        return '#7D7D7D'
      case Metal.PB:
        return '#5C6BC0'
      default:
        return '#757575'
    }
  }

  const getStageColor = (stage: ProjectStage) => {
    switch (stage) {
      case ProjectStage.PRE_DISCOVERY:
        return 'default'
      case ProjectStage.BROWNFIELD_EXPLORATION:
        return 'info'
      case ProjectStage.MAIDEN_MRE:
        return 'primary'
      case ProjectStage.SCOPING_PEA:
        return 'secondary'
      case ProjectStage.PFS:
        return 'warning'
      case ProjectStage.FS:
        return 'success'
      case ProjectStage.FID_BUILD:
        return 'error'
      default:
        return 'default'
    }
  }

  const formatResources = (project: Project) => {
    if (!project.resources || project.resources.length === 0) return 'N/A'
    
    // Get M+I content for primary metal only
    const miResources = project.resources.filter(r =>
      r.metal === project.primaryMetal && (r.category === 'MEASURED' || r.category === 'INDICATED')
    )
    const totalContent = miResources.reduce((sum, r) => sum + r.content, 0)
    
    if (totalContent === 0) return 'N/A'
    
    const unit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const metalSymbol = formatMetalSymbol(project.primaryMetal)
    
    return `${n(totalContent)} ${unit} ${metalSymbol}`
  }

  const formatReserves = (project: Project) => {
    if (!project.reserves || project.reserves.length === 0) return 'N/A'
    
    // Get total content for primary metal only
    const primaryReserves = project.reserves.filter(r => r.metal === project.primaryMetal)
    const totalContent = primaryReserves.reduce((sum, reserve) => sum + reserve.content, 0)
    
    if (totalContent === 0) return 'N/A'
    
    const unit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const metalSymbol = formatMetalSymbol(project.primaryMetal)
    
    return `${n(totalContent)} ${unit} ${metalSymbol}`
  }

  const getReservesTooltip = (project: Project) => {
    if (!project.reserves || project.reserves.length === 0) return <Box>No reserve data</Box>
    
    const primaryUnit = project.primaryMetal === Metal.CU ? '%' : 'g/t'
    const primaryContentUnit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const tonnageUnit = 'kt'
    
    // Group primary metal by category
    const primaryProven = project.reserves.find(r => r.category === 'PROVEN' && r.metal === project.primaryMetal)
    const primaryProbable = project.reserves.find(r => r.category === 'PROBABLE' && r.metal === project.primaryMetal)
    
    // Calculate primary metal totals (P+P)
    const primaryPpTonnage = (primaryProven?.tonnage || 0) + (primaryProbable?.tonnage || 0)
    const primaryPpContent = (primaryProven?.content || 0) + (primaryProbable?.content || 0)
    const primaryPpGrade = primaryPpTonnage > 0
      ? project.primaryMetal === Metal.CU
        ? (primaryPpContent / primaryPpTonnage) * 100
        : (primaryPpContent * 31.1035) / primaryPpTonnage
      : 0
    
    // Get secondary metals reserves
    const secondaryMetalsData = project.secondaryMetals.map(metal => {
      const unit = metal === Metal.CU ? '%' : 'g/t'
      const contentUnit = metal === Metal.CU ? 'kt' : 'koz'
      
      const proven = project.reserves.find(r => r.category === 'PROVEN' && r.metal === metal)
      const probable = project.reserves.find(r => r.category === 'PROBABLE' && r.metal === metal)
      
      const ppTonnage = (proven?.tonnage || 0) + (probable?.tonnage || 0)
      const ppContent = (proven?.content || 0) + (probable?.content || 0)
      const ppGrade = ppTonnage > 0
        ? metal === Metal.CU
          ? (ppContent / ppTonnage) * 100
          : (ppContent * 31.1035) / ppTonnage
        : 0
      
      return {
        metal,
        unit,
        contentUnit,
        proven,
        probable,
        ppTonnage,
        ppContent,
        ppGrade
      }
    }).filter(data => data.proven || data.probable)
    
    return (
      <Box sx={{ display: 'flex', gap: 3, minWidth: 400 }}>
        {/* Primary Metal Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            {t(`projects.metals.${project.primaryMetal}`)} (Primary)
          </Typography>
          
          {primaryProven && (
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
              <strong>Proven:</strong><br />
              {n(primaryProven.tonnage)} {tonnageUnit} × {primaryProven.grade.toFixed(2)} {primaryUnit} = {n(primaryProven.content)} {primaryContentUnit}
            </Typography>
          )}
          
          {primaryProbable && (
            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
              <strong>Probable:</strong><br />
              {n(primaryProbable.tonnage)} {tonnageUnit} × {primaryProbable.grade.toFixed(2)} {primaryUnit} = {n(primaryProbable.content)} {primaryContentUnit}
            </Typography>
          )}
          
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
              P+P: {n(primaryPpTonnage)} {tonnageUnit} × {primaryPpGrade.toFixed(2)} {primaryUnit} = {n(primaryPpContent)} {primaryContentUnit}
            </Typography>
          </Box>
        </Box>
        
        {/* Secondary Metals Column */}
        {secondaryMetalsData.length > 0 && (
          <Box sx={{ flex: 1, borderLeft: 2, borderColor: 'divider', pl: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
              Secondary Metals
            </Typography>
            
            {secondaryMetalsData.map((data) => (
              <Box key={data.metal} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                  {t(`projects.metals.${data.metal}`)}
                </Typography>
                
                {data.proven && (
                  <Typography variant="body2" sx={{ mb: 0.3, fontSize: '0.75rem' }}>
                    <strong>P:</strong> {n(data.proven.tonnage)} {tonnageUnit} × {data.proven.grade.toFixed(2)} {data.unit} = {n(data.proven.content)} {data.contentUnit}
                  </Typography>
                )}
                
                {data.probable && (
                  <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                    <strong>Prob:</strong> {n(data.probable.tonnage)} {tonnageUnit} × {data.probable.grade.toFixed(2)} {data.unit} = {n(data.probable.content)} {data.contentUnit}
                  </Typography>
                )}
                
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
                    P+P: {n(data.ppTonnage)} {tonnageUnit} × {data.ppGrade.toFixed(2)} {data.unit} = {n(data.ppContent)} {data.contentUnit}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  const formatReservesGrade = (project: Project) => {
    if (!project.reserves || project.reserves.length === 0) return 'N/A'
    
    // Get P+P reserves for primary metal
    const proven = project.reserves.find(r => r.category === 'PROVEN' && r.metal === project.primaryMetal)
    const probable = project.reserves.find(r => r.category === 'PROBABLE' && r.metal === project.primaryMetal)
    
    // Calculate P+P totals
    const ppTonnage = (proven?.tonnage || 0) + (probable?.tonnage || 0)
    const ppContent = (proven?.content || 0) + (probable?.content || 0)
    
    if (ppTonnage === 0) return 'N/A'
    
    // Calculate weighted average grade
    const unit = project.primaryMetal === Metal.CU ? '%' : 'g/t'
    const ppGrade = project.primaryMetal === Metal.CU 
      ? (ppContent / ppTonnage) * 100  // Convert back to %
      : (ppContent * 31.1035) / ppTonnage  // Convert back to g/t
    
    return `${ppGrade.toFixed(2)} ${unit}`
  }

  const getResourcesTooltip = (project: Project) => {
    if (!project.resources || project.resources.length === 0) return <Box>No resource data</Box>
    
    // Primary metal data
    const primaryUnit = project.primaryMetal === Metal.CU ? '%' : 'g/t'
    const primaryContentUnit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const tonnageUnit = 'kt'
    
    // Group primary metal by category
    const primaryMeasured = project.resources.find(r => r.category === 'MEASURED' && r.metal === project.primaryMetal)
    const primaryIndicated = project.resources.find(r => r.category === 'INDICATED' && r.metal === project.primaryMetal)
    const primaryInferred = project.resources.find(r => r.category === 'INFERRED' && r.metal === project.primaryMetal)
    
    // Calculate primary metal totals
    const primaryMiTonnage = (primaryMeasured?.tonnage || 0) + (primaryIndicated?.tonnage || 0)
    const primaryMiContent = (primaryMeasured?.content || 0) + (primaryIndicated?.content || 0)
    const primaryMiGrade = primaryMiTonnage > 0
      ? project.primaryMetal === Metal.CU
        ? (primaryMiContent / primaryMiTonnage) * 100
        : (primaryMiContent * 31.1035) / primaryMiTonnage
      : 0
    
    const primaryMiiTonnage = primaryMiTonnage + (primaryInferred?.tonnage || 0)
    const primaryMiiContent = primaryMiContent + (primaryInferred?.content || 0)
    const primaryMiiGrade = primaryMiiTonnage > 0
      ? project.primaryMetal === Metal.CU
        ? (primaryMiiContent / primaryMiiTonnage) * 100
        : (primaryMiiContent * 31.1035) / primaryMiiTonnage
      : 0
    
    // Get secondary metals resources
    const secondaryMetalsData = project.secondaryMetals.map(metal => {
      const unit = metal === Metal.CU ? '%' : 'g/t'
      const contentUnit = metal === Metal.CU ? 'kt' : 'koz'
      
      const measured = project.resources.find(r => r.category === 'MEASURED' && r.metal === metal)
      const indicated = project.resources.find(r => r.category === 'INDICATED' && r.metal === metal)
      const inferred = project.resources.find(r => r.category === 'INFERRED' && r.metal === metal)
      
      const miTonnage = (measured?.tonnage || 0) + (indicated?.tonnage || 0)
      const miContent = (measured?.content || 0) + (indicated?.content || 0)
      const miGrade = miTonnage > 0
        ? metal === Metal.CU
          ? (miContent / miTonnage) * 100
          : (miContent * 31.1035) / miTonnage
        : 0
      
      const miiTonnage = miTonnage + (inferred?.tonnage || 0)
      const miiContent = miContent + (inferred?.content || 0)
      const miiGrade = miiTonnage > 0
        ? metal === Metal.CU
          ? (miiContent / miiTonnage) * 100
          : (miiContent * 31.1035) / miiTonnage
        : 0
      
      return {
        metal,
        unit,
        contentUnit,
        measured,
        indicated,
        inferred,
        miTonnage,
        miContent,
        miGrade,
        miiTonnage,
        miiContent,
        miiGrade
      }
    }).filter(data => data.measured || data.indicated || data.inferred)
    
    return (
      <Box sx={{ display: 'flex', gap: 3, minWidth: 400 }}>
        {/* Primary Metal Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            {t(`projects.metals.${project.primaryMetal}`)} (Primary)
          </Typography>
          
          {primaryMeasured && (
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
              <strong>Measured:</strong><br />
              {n(primaryMeasured.tonnage)} {tonnageUnit} × {primaryMeasured.grade.toFixed(2)} {primaryUnit} = {n(primaryMeasured.content)} {primaryContentUnit}
            </Typography>
          )}
          
          {primaryIndicated && (
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
              <strong>Indicated:</strong><br />
              {n(primaryIndicated.tonnage)} {tonnageUnit} × {primaryIndicated.grade.toFixed(2)} {primaryUnit} = {n(primaryIndicated.content)} {primaryContentUnit}
            </Typography>
          )}
          
          {primaryInferred && (
            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
              <strong>Inferred:</strong><br />
              {n(primaryInferred.tonnage)} {tonnageUnit} × {primaryInferred.grade.toFixed(2)} {primaryUnit} = {n(primaryInferred.content)} {primaryContentUnit}
            </Typography>
          )}
          
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
              M+I: {n(primaryMiTonnage)} {tonnageUnit} × {primaryMiGrade.toFixed(2)} {primaryUnit} = {n(primaryMiContent)} {primaryContentUnit}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
              M+I+I: {n(primaryMiiTonnage)} {tonnageUnit} × {primaryMiiGrade.toFixed(2)} {primaryUnit} = {n(primaryMiiContent)} {primaryContentUnit}
            </Typography>
          </Box>
        </Box>
        
        {/* Secondary Metals Column */}
        {secondaryMetalsData.length > 0 && (
          <Box sx={{ flex: 1, borderLeft: 2, borderColor: 'divider', pl: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
              Secondary Metals
            </Typography>
            
            {secondaryMetalsData.map((data) => (
              <Box key={data.metal} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                  {t(`projects.metals.${data.metal}`)}
                </Typography>
                
                {data.measured && (
                  <Typography variant="body2" sx={{ mb: 0.3, fontSize: '0.75rem' }}>
                    <strong>M:</strong> {n(data.measured.tonnage)} {tonnageUnit} × {data.measured.grade.toFixed(2)} {data.unit} = {n(data.measured.content)} {data.contentUnit}
                  </Typography>
                )}
                
                {data.indicated && (
                  <Typography variant="body2" sx={{ mb: 0.3, fontSize: '0.75rem' }}>
                    <strong>I:</strong> {n(data.indicated.tonnage)} {tonnageUnit} × {data.indicated.grade.toFixed(2)} {data.unit} = {n(data.indicated.content)} {data.contentUnit}
                  </Typography>
                )}
                
                {data.inferred && (
                  <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                    <strong>Inf:</strong> {n(data.inferred.tonnage)} {tonnageUnit} × {data.inferred.grade.toFixed(2)} {data.unit} = {n(data.inferred.content)} {data.contentUnit}
                  </Typography>
                )}
                
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
                    M+I+I: {n(data.miiTonnage)} {tonnageUnit} × {data.miiGrade.toFixed(2)} {data.unit} = {n(data.miiContent)} {data.contentUnit}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  const formatGrade = (project: Project) => {
    if (!project.resources || project.resources.length === 0) return 'N/A'
    
    // Get M+I resources for primary metal
    const measured = project.resources.find(r => r.category === 'MEASURED' && r.metal === project.primaryMetal)
    const indicated = project.resources.find(r => r.category === 'INDICATED' && r.metal === project.primaryMetal)
    
    // Calculate M+I totals
    const miTonnage = (measured?.tonnage || 0) + (indicated?.tonnage || 0)
    const miContent = (measured?.content || 0) + (indicated?.content || 0)
    
    if (miTonnage === 0) return 'N/A'
    
    // Calculate weighted average grade
    // For Cu: grade is in %, so content = tonnage * grade / 100
    // For Au/Ag: grade is in g/t, so content (koz) = tonnage (kt) * grade (g/t) / 31.1035
    const unit = project.primaryMetal === Metal.CU ? '%' : 'g/t'
    const miGrade = project.primaryMetal === Metal.CU 
      ? (miContent / miTonnage) * 100  // Convert back to %
      : (miContent * 31.1035) / miTonnage  // Convert back to g/t
    
    return `${miGrade.toFixed(2)} ${unit}`
  }

  const formatInferredResources = (project: Project) => {
    if (!project.resources || project.resources.length === 0) return 'N/A'
    const inferred = project.resources.filter(r =>
      r.metal === project.primaryMetal && r.category === 'INFERRED'
    )
    const totalContent = inferred.reduce((sum, r) => sum + r.content, 0)
    if (totalContent === 0) return 'N/A'
    const unit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const metalSymbol = formatMetalSymbol(project.primaryMetal)
    return `${n(totalContent)} ${unit} ${metalSymbol}`
  }

  const getInferredResourcesTooltip = (project: Project) => {
    if (!project.resources || project.resources.length === 0) return <Box>No inferred resource data</Box>
    const primaryUnit = project.primaryMetal === Metal.CU ? '%' : 'g/t'
    const primaryContentUnit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const tonnageUnit = 'kt'

    const primaryInferred = project.resources.find(r => r.category === 'INFERRED' && r.metal === project.primaryMetal)
    const secondaryData = project.secondaryMetals.map(metal => {
      const unit = metal === Metal.CU ? '%' : 'g/t'
      const contentUnit = metal === Metal.CU ? 'kt' : 'koz'
      const inferred = project.resources.find(r => r.category === 'INFERRED' && r.metal === metal)
      return { metal, unit, contentUnit, inferred }
    }).filter(d => d.inferred)

    if (!primaryInferred && secondaryData.length === 0) return <Box>No inferred resource data</Box>

    return (
      <Box sx={{ display: 'flex', gap: 3, minWidth: 300 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            {t(`projects.metals.${project.primaryMetal}`)} (Primary)
          </Typography>
          {primaryInferred ? (
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {n(primaryInferred.tonnage)} {tonnageUnit} × {primaryInferred.grade.toFixed(2)} {primaryUnit} = {n(primaryInferred.content)} {primaryContentUnit}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>N/A</Typography>
          )}
        </Box>
        {secondaryData.length > 0 && (
          <Box sx={{ flex: 1, borderLeft: 2, borderColor: 'divider', pl: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
              Secondary Metals
            </Typography>
            {secondaryData.map(d => (
              <Box key={d.metal} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'text.secondary' }}>
                  {t(`projects.metals.${d.metal}`)}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  {n(d.inferred!.tonnage)} {tonnageUnit} × {d.inferred!.grade.toFixed(2)} {d.unit} = {n(d.inferred!.content)} {d.contentUnit}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  const formatHistoricalProduction = (project: Project) => {
    if (!project.production || project.production.length === 0) return 'N/A'
    
    // Get most recent historical production for primary metal (periods without "E")
    const primaryProduction = project.production
      .filter(p => p.metal === project.primaryMetal && !p.period.endsWith('E'))
      .sort((a, b) => b.period.localeCompare(a.period))[0]
    
    if (!primaryProduction) return 'N/A'
    
    const unit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const metalSymbol = formatMetalSymbol(project.primaryMetal)
    
    return {
      value: `${n(primaryProduction.production ?? 0)} ${unit} ${metalSymbol}`,
      period: primaryProduction.period
    }
  }

  const getHistoricalProductionTooltip = (project: Project) => {
    if (!project.production || project.production.length === 0) {
      return <Box>No historical production data</Box>
    }
    
    // Filter only historical data (without "E")
    const historicalData = project.production.filter(p => !p.period.endsWith('E'))
    
    if (historicalData.length === 0) {
      return <Box>No historical production data</Box>
    }
    
    // Group by metal
    const byMetal = historicalData.reduce((acc, prod) => {
      if (!acc[prod.metal]) acc[prod.metal] = []
      acc[prod.metal].push(prod)
      return acc
    }, {} as Record<string, typeof historicalData>)
    
    // Sort each metal's data by period (most recent first)
    Object.keys(byMetal).forEach(metal => {
      byMetal[metal].sort((a, b) => b.period.localeCompare(a.period))
    })
    
    return (
      <Box sx={{ minWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Historical Production
        </Typography>
        
        {Object.entries(byMetal).map(([metal, productions]) => {
          const unit = metal === 'CU' ? 'kt' : 'koz'
          const metalSymbol = formatMetalSymbol(metal as Metal)
          const isPrimary = metal === project.primaryMetal
          
          return (
            <Box key={metal} sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 0.5, 
                  color: isPrimary ? 'primary.main' : 'text.secondary' 
                }}
              >
                {metalSymbol} {isPrimary && '(Primary)'}
              </Typography>
              
              {productions.map((prod, idx) => (
                <Typography 
                  key={idx} 
                  variant="body2" 
                  sx={{ fontSize: '0.85rem', ml: 1 }}
                >
                  {prod.period}: {getRecordDisplay(prod, unit)}
                </Typography>
              ))}
            </Box>
          )
        })}
      </Box>
    )
  }

  const formatGuidance = (project: Project) => {
    if (!project.production || project.production.length === 0) return 'N/A'
    
    // Get guidance for primary metal (periods with "E"), prioritize nearest time period
    const primaryGuidance = project.production
      .filter(g => g.metal === project.primaryMetal && g.period.endsWith('E'))
      .sort((a, b) => {
        // Remove "E" for comparison
        const aPeriod = a.period.slice(0, -1)
        const bPeriod = b.period.slice(0, -1)
        // Sort by period: quarters first (Q12026), then years (2026)
        if (aPeriod.startsWith('Q') && !bPeriod.startsWith('Q')) return -1
        if (!aPeriod.startsWith('Q') && bPeriod.startsWith('Q')) return 1
        return aPeriod.localeCompare(bPeriod)
      })[0]
    
    if (!primaryGuidance) return 'N/A'
    
    const unit = project.primaryMetal === Metal.CU ? 'kt' : 'koz'
    const metalSymbol = formatMetalSymbol(project.primaryMetal)
    const displayVal = primaryGuidance.min != null && primaryGuidance.max != null
      ? `${n((primaryGuidance.min + primaryGuidance.max) / 2)} ${unit} ${metalSymbol}`
      : `${n(primaryGuidance.production ?? 0)} ${unit} ${metalSymbol}`
    
    return {
      value: displayVal,
      period: primaryGuidance.period
    }
  }

  const getGuidanceTooltip = (project: Project) => {
    if (!project.production || project.production.length === 0) {
      return <Box>No guidance data</Box>
    }
    
    // Filter only guidance data (with "E")
    const guidanceData = project.production.filter(p => p.period.endsWith('E'))
    
    if (guidanceData.length === 0) {
      return <Box>No guidance data</Box>
    }
    
    // Group by metal
    const byMetal = guidanceData.reduce((acc, guide) => {
      if (!acc[guide.metal]) acc[guide.metal] = []
      acc[guide.metal].push(guide)
      return acc
    }, {} as Record<string, typeof guidanceData>)
    
    // Sort each metal's data by period
    Object.keys(byMetal).forEach(metal => {
      byMetal[metal].sort((a, b) => {
        const aPeriod = a.period.slice(0, -1)
        const bPeriod = b.period.slice(0, -1)
        if (aPeriod.startsWith('Q') && !bPeriod.startsWith('Q')) return -1
        if (!aPeriod.startsWith('Q') && bPeriod.startsWith('Q')) return 1
        return aPeriod.localeCompare(bPeriod)
      })
    })
    
    return (
      <Box sx={{ minWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Production Guidance
        </Typography>
        
        {Object.entries(byMetal).map(([metal, guidances]) => {
          const unit = metal === 'CU' ? 'kt' : 'koz'
          const metalSymbol = formatMetalSymbol(metal as Metal)
          const isPrimary = metal === project.primaryMetal
          
          return (
            <Box key={metal} sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 0.5, 
                  color: isPrimary ? 'primary.main' : 'text.secondary' 
                }}
              >
                {metalSymbol} {isPrimary && '(Primary)'}
              </Typography>
              
              {guidances.map((guide, idx) => (
                <Typography 
                  key={idx} 
                  variant="body2" 
                  sx={{ fontSize: '0.85rem', ml: 1 }}
                >
                  {guide.period}: {getRecordDisplay(guide, unit)}
                </Typography>
              ))}
            </Box>
          )
        })}
      </Box>
    )
  }

  const getCostUnit = (metal: Metal) => metal === Metal.CU ? '$/t' : '$/oz'

  // Returns display string: range if min/max, otherwise the point value
  const getRecordDisplay = (rec: { production?: number; cost?: number; min?: number; max?: number }, unit: string) => {
    if (rec.min != null && rec.max != null) return `${n(rec.min)}–${n(rec.max)} ${unit}`
    const val = rec.production ?? rec.cost ?? 0
    return `${n(val)} ${unit}`
  }

  const formatCostData = (costData: Project['aisc'] | Project['cashCost'], isGuidance: boolean, primaryMetal: Metal) => {
    if (!costData || costData.length === 0) return null

    const filtered = costData.filter(c =>
      c.metal === primaryMetal &&
      (isGuidance ? c.period.endsWith('E') : !c.period.endsWith('E'))
    )

    if (filtered.length === 0) return null

    const mostRecent = filtered.sort((a, b) => {
      const aPeriod = a.period.replace('E', '')
      const bPeriod = b.period.replace('E', '')
      return bPeriod.localeCompare(aPeriod)
    })[0]

    const unit = getCostUnit(primaryMetal)
    let cellValue: string
    if (isGuidance && mostRecent.min != null && mostRecent.max != null) {
      const mid = parseFloat(((mostRecent.min + mostRecent.max) / 2).toFixed(2))
      cellValue = `${n(mid)} ${unit}`
    } else {
      cellValue = `${n(mostRecent.cost ?? 0)} ${unit}`
    }
    return { value: cellValue, period: mostRecent.period }
  }

  const formatAiscHist = (project: Project) => formatCostData(project.aisc, false, project.primaryMetal)
  const formatAiscGuidance = (project: Project) => formatCostData(project.aisc, true, project.primaryMetal)
  const formatCashCostHist = (project: Project) => formatCostData(project.cashCost, false, project.primaryMetal)
  const formatCashCostGuidance = (project: Project) => formatCostData(project.cashCost, true, project.primaryMetal)

  const getCostTooltip = (costData: Project['aisc'] | Project['cashCost'], isGuidance: boolean, primaryMetal: Metal, title: string) => {
    if (!costData || costData.length === 0) return <Box>No data</Box>

    const filtered = costData.filter(c => isGuidance ? c.period.endsWith('E') : !c.period.endsWith('E'))
    if (filtered.length === 0) return <Box>No data</Box>

    const byMetal = filtered.reduce((acc, c) => {
      if (!acc[c.metal]) acc[c.metal] = []
      acc[c.metal].push(c)
      return acc
    }, {} as Record<string, typeof filtered>)

    Object.keys(byMetal).forEach(metal => {
      byMetal[metal].sort((a, b) => b.period.replace('E', '').localeCompare(a.period.replace('E', '')))
    })

    return (
      <Box sx={{ minWidth: 250 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{title}</Typography>
        {Object.entries(byMetal).map(([metal, records]) => {
          const isPrimary = metal === primaryMetal
          return (
            <Box key={metal} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: isPrimary ? 'primary.main' : 'text.secondary' }}>
                {formatMetalSymbol(metal as Metal)} {isPrimary && '(Primary)'}
              </Typography>
              {records.map((rec, idx) => (
                <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', ml: 1 }}>
                  {rec.period}: {
                    isGuidance && rec.min != null && rec.max != null
                      ? `${n(rec.min)}–${n(rec.max)} ${getCostUnit(metal as Metal)}`
                      : `${n(rec.cost ?? 0)} ${getCostUnit(metal as Metal)}`
                  }
                </Typography>
              ))}
            </Box>
          )
        })}
      </Box>
    )
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message || t('common.error')}
      </Alert>
    )
  }

  if (!sortedProjects || sortedProjects.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          {t('common.noResults')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with result count and pagination */}
      <Box display="flex" justifyContent="space-between" alignItems="center" px={1} py={1}>
        <Typography variant="body2" color="text.secondary">
          {sortedProjects.length} {t('projects.title').toLowerCase()}
        </Typography>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            color="primary"
          />
        )}
      </Box>
      <TableContainer component={Paper} elevation={1} sx={{ flex: 1 }}>
        <Table stickyHeader>
          <TableHead>
            {/* Group headers row */}
            <TableRow>
              <TableCell 
                padding="checkbox" 
                rowSpan={2}
                sx={{ 
                  borderBottom: 'none',
                  verticalAlign: 'bottom',
                  borderRight: '2px solid',
                  borderRightColor: 'divider'
                }}
              >
                {/* Favorites column */}
              </TableCell>
              <TableCell 
                colSpan={6}
                align="center"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  borderRight: '2px solid',
                  borderRightColor: 'divider'
                }}
              >
                {t('projects.table.groups.information')}
              </TableCell>
              <TableCell 
                colSpan={5}
                align="center"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  borderRight: '2px solid',
                  borderRightColor: 'divider'
                }}
              >
                {t('projects.table.groups.reservesAndResources')}
              </TableCell>
              <TableCell 
                colSpan={2}
                align="center"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  borderRight: '2px solid',
                  borderRightColor: 'divider'
                }}
              >
                {t('projects.table.groups.operatingMetrics')}
              </TableCell>
              <TableCell 
                colSpan={4}
                align="center"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}
              >
                {t('projects.table.groups.financialMetrics')}
              </TableCell>
            </TableRow>
            
            {/* Column headers row */}
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('projects.table.columns.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'operators'}
                  direction={sortField === 'operators' ? sortDirection : 'asc'}
                  onClick={() => handleSort('operators')}
                >
                  {t('projects.table.columns.operator')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
              <TableSortLabel
                active={sortField === 'country'}
                direction={sortField === 'country' ? sortDirection : 'asc'}
                onClick={() => handleSort('country')}
              >
                {t('projects.table.columns.country')}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'primaryMetal'}
                direction={sortField === 'primaryMetal' ? sortDirection : 'asc'}
                onClick={() => handleSort('primaryMetal')}
              >
                {t('projects.table.columns.metals')}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'stage'}
                direction={sortField === 'stage' ? sortDirection : 'asc'}
                onClick={() => handleSort('stage')}
              >
                {t('projects.table.columns.stage')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
              {t('projects.table.columns.firstProduction')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.reserves')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.reservesGrade')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.resources')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.grade')}
            </TableCell>
            <TableCell align="right" sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
              {t('projects.table.columns.inferredResources')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.historical')}
            </TableCell>
            <TableCell align="right" sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
              {t('projects.table.columns.guidance')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.aiscHistorical')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.aiscGuidance')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.cashCostHistorical')}
            </TableCell>
            <TableCell align="right">
              {t('projects.table.columns.cashCostGuidance')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedProjects.map((project) => (
            <TableRow
              key={project.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onProjectSelect?.(project)}
            >
              <TableCell padding="checkbox" sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleFavorite?.(project.id, !project.isFavorite)
                      }}
                      color={project.isFavorite ? 'error' : 'default'}
                      title={project.isFavorite ? t('common.unfavorite') : t('common.favorite')}
                    >
                      {project.isFavorite ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  <IconButton
                      size="small"
                      onClick={(e) => handleOpenWatchlistModal(e, project)}
                      color="default"
                      title={t('projects.watchlists.addToList')}
                    >
                      <PlaylistAdd />
                    </IconButton>
                </Stack>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {project.name}
                </Typography>
              </TableCell>
              
              <TableCell>
                {(() => {
                  const owners = project.owners && project.owners.length > 0
                    ? project.owners
                    : []
                  const primaryOwner = owners.length > 0
                    ? owners.reduce((prev: typeof owners[0], curr: typeof owners[0]) => Number(curr.ownership) > Number(prev.ownership) ? curr : prev)
                    : null
                  if (!primaryOwner) return null
                  
                  return (
                    <>
                      <Box
                        component="span"
                        onMouseEnter={(e) => owners.length > 1 ? handleOperatorsPopoverOpen(e, project.id) : null}
                        onMouseLeave={() => owners.length > 1 ? handleOperatorsPopoverClose(project.id) : null}
                        sx={{ 
                          cursor: owners.length > 1 ? 'help' : 'default',
                          display: 'inline-block',
                          ...(owners.length > 1 && { borderBottom: '1px dotted rgba(0, 0, 0, 0.3)' }),
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {primaryOwner.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {Number(primaryOwner.ownership)}%
                          </Typography>
                        </Box>
                      </Box>
                      {owners.length > 1 && (
                        <Popover
                          open={Boolean(operatorsAnchorEl[project.id])}
                          anchorEl={operatorsAnchorEl[project.id]}
                          onClose={() => handleOperatorsPopoverClose(project.id)}
                          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                          disableRestoreFocus
                          sx={{ pointerEvents: 'none' }}
                          PaperProps={{ sx: { pointerEvents: 'auto', p: 2, maxWidth: 400 } }}
                        >
                          <Box sx={{ minWidth: 250 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              All Owners
                            </Typography>
                            {[...owners]
                              .sort((a, b) => Number(b.ownership) - Number(a.ownership))
                              .map((owner, index) => (
                                <Box
                                  key={owner.id}
                                  sx={{
                                    mb: 1,
                                    pb: index < owners.length - 1 ? 1 : 0,
                                    borderBottom: index < owners.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider'
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: index === 0 ? 'bold' : 'normal', color: index === 0 ? 'primary.main' : 'text.primary' }}
                                  >
                                    {owner.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Ownership: {Number(owner.ownership)}%
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Popover>
                      )}
                    </>
                  )
                })()}
              </TableCell>
              
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2">
                    {t(`projects.countries.${project.country}`)}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                  <Chip
                    size="small"
                    label={formatMetalSymbol(project.primaryMetal)}
                    sx={{
                      backgroundColor: getMetalColor(project.primaryMetal),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                  {project.secondaryMetals.map((metal: Metal) => (
                    <Chip
                      key={metal}
                      size="small"
                      variant="outlined"
                      label={formatMetalSymbol(metal)}
                      sx={{
                        borderColor: getMetalColor(metal),
                        color: getMetalColor(metal),
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                </Stack>
              </TableCell>
              
              <TableCell>
                <Chip
                  size="small"
                  color={getStageColor(project.stage)}
                  label={t(`projects.stages.${project.stage}`)}
                  variant="outlined"
                />
              </TableCell>
              
              <TableCell sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
                <Typography variant="body2">
                  {formatFirstProduction(project.firstProductionDate, project.firstProductionPrecision)}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Box
                  component="span"
                  onMouseEnter={(e) => handleReservesPopoverOpen(e, project.id)}
                  onMouseLeave={() => handleReservesPopoverClose(project.id)}
                  sx={{ 
                    cursor: 'help',
                    display: 'inline-block',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    component="span"
                  >
                    {formatReserves(project)}
                  </Typography>
                </Box>
                <Popover
                  open={Boolean(reservesAnchorEl[project.id])}
                  anchorEl={reservesAnchorEl[project.id]}
                  onClose={() => handleReservesPopoverClose(project.id)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                  }}
                  PaperProps={{
                    sx: {
                      pointerEvents: 'auto',
                      p: 2,
                      maxWidth: 800,
                    }
                  }}
                >
                  {getReservesTooltip(project)}
                </Popover>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {formatReservesGrade(project)}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Box
                  component="span"
                  onMouseEnter={(e) => handleResourcesPopoverOpen(e, project.id)}
                  onMouseLeave={() => handleResourcesPopoverClose(project.id)}
                  sx={{ 
                    cursor: 'help',
                    display: 'inline-block',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    component="span"
                  >
                    {formatResources(project)}
                  </Typography>
                </Box>
                <Popover
                  open={Boolean(resourcesAnchorEl[project.id])}
                  anchorEl={resourcesAnchorEl[project.id]}
                  onClose={() => handleResourcesPopoverClose(project.id)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                  }}
                  PaperProps={{
                    sx: {
                      pointerEvents: 'auto',
                      p: 2,
                      maxWidth: 800,
                    }
                  }}
                >
                  {getResourcesTooltip(project)}
                </Popover>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {formatGrade(project)}
                </Typography>
              </TableCell>
              
              <TableCell align="right" sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
                {(() => {
                  const infRes = formatInferredResources(project)
                  if (infRes === 'N/A') return <Typography variant="body2">N/A</Typography>
                  const hasSecondary = project.secondaryMetals.some((m: Metal) =>
                    project.resources?.some((r: {category: string; metal: Metal}) => r.category === 'INFERRED' && r.metal === m)
                  )
                  return (
                    <>
                      <Box
                        component="span"
                        onMouseEnter={(e) => handleInferredResourcesPopoverOpen(e, project.id)}
                        onMouseLeave={() => handleInferredResourcesPopoverClose(project.id)}
                        sx={{
                          cursor: 'help',
                          display: 'inline-block',
                          ...(hasSecondary && { borderBottom: '1px dotted rgba(0, 0, 0, 0.3)' }),
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium" component="span">
                          {infRes}
                        </Typography>
                      </Box>
                      <Popover
                        open={Boolean(inferredResourcesAnchorEl[project.id])}
                        anchorEl={inferredResourcesAnchorEl[project.id]}
                        onClose={() => handleInferredResourcesPopoverClose(project.id)}
                        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                        disableRestoreFocus
                        sx={{ pointerEvents: 'none' }}
                        PaperProps={{ sx: { pointerEvents: 'auto', p: 2, maxWidth: 600 } }}
                      >
                        {getInferredResourcesTooltip(project)}
                      </Popover>
                    </>
                  )
                })()}
              </TableCell>
              
              <TableCell align="right">
                {(() => {
                  const historical = formatHistoricalProduction(project)
                  if (historical === 'N/A') {
                    return <Typography variant="body2">N/A</Typography>
                  }
                  const hasMultipleHist = (project.production?.filter((p: {period: string}) => !p.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box
                      component="span"
                      onMouseEnter={(e) => handleHistoricalPopoverOpen(e, project.id)}
                      onMouseLeave={() => handleHistoricalPopoverClose(project.id)}
                      sx={{ 
                        cursor: 'help',
                        display: 'inline-block',
                        ...(hasMultipleHist && { borderBottom: '1px dotted rgba(0, 0, 0, 0.3)' }),
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {historical.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {historical.period}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover
                  open={Boolean(historicalAnchorEl[project.id])}
                  anchorEl={historicalAnchorEl[project.id]}
                  onClose={() => handleHistoricalPopoverClose(project.id)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                  }}
                  PaperProps={{
                    sx: {
                      pointerEvents: 'auto',
                      p: 2,
                      maxWidth: 400,
                    }
                  }}
                >
                  {getHistoricalProductionTooltip(project)}
                </Popover>
              </TableCell>
              
              <TableCell align="right" sx={{ borderRight: '2px solid', borderRightColor: 'divider' }}>
                {(() => {
                  const guidance = formatGuidance(project)
                  if (guidance === 'N/A') {
                    return <Typography variant="body2">N/A</Typography>
                  }
                  const hasMultipleGuid = (project.production?.filter((p: {period: string}) => p.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box
                      component="span"
                      onMouseEnter={(e) => handleGuidancePopoverOpen(e, project.id)}
                      onMouseLeave={() => handleGuidancePopoverClose(project.id)}
                      sx={{ 
                        cursor: 'help',
                        display: 'inline-block',
                        ...(hasMultipleGuid && { borderBottom: '1px dotted rgba(0, 0, 0, 0.3)' }),
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {guidance.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {guidance.period}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover
                  open={Boolean(guidanceAnchorEl[project.id])}
                  anchorEl={guidanceAnchorEl[project.id]}
                  onClose={() => handleGuidancePopoverClose(project.id)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                  }}
                  PaperProps={{
                    sx: {
                      pointerEvents: 'auto',
                      p: 2,
                      maxWidth: 400,
                    }
                  }}
                >
                  {getGuidanceTooltip(project)}
                </Popover>
              </TableCell>
              

              <TableCell align="right">
                {(() => {
                  const aiscHist = formatAiscHist(project)
                  if (!aiscHist) return <Typography variant="body2">N/A</Typography>
                  const hasMultipleAiscHist = (project.aisc?.filter((c: {period: string}) => !c.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box component="span" onMouseEnter={(e) => handleAiscHistPopoverOpen(e, project.id)} onMouseLeave={() => handleAiscHistPopoverClose(project.id)} sx={{ cursor: "help", display: "inline-block", ...(hasMultipleAiscHist && { borderBottom: "1px dotted rgba(0, 0, 0, 0.3)" }) }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="body2" fontWeight="medium">{aiscHist.value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>{aiscHist.period}</Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover open={Boolean(aiscHistAnchorEl[project.id])} anchorEl={aiscHistAnchorEl[project.id]} onClose={() => handleAiscHistPopoverClose(project.id)} anchorOrigin={{ vertical: "center", horizontal: "left" }} transformOrigin={{ vertical: "center", horizontal: "right" }} disableRestoreFocus sx={{ pointerEvents: "none" }} PaperProps={{ sx: { pointerEvents: "auto", p: 2, maxWidth: 400 } }}>
                  {getCostTooltip(project.aisc, false, project.primaryMetal, "AISC Historical")}
                </Popover>
              </TableCell>

              <TableCell align="right">
                {(() => {
                  const aiscGuid = formatAiscGuidance(project)
                  if (!aiscGuid) return <Typography variant="body2">N/A</Typography>
                  const hasMultipleAiscGuid = (project.aisc?.filter((c: {period: string}) => c.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box component="span" onMouseEnter={(e) => handleAiscGuidancePopoverOpen(e, project.id)} onMouseLeave={() => handleAiscGuidancePopoverClose(project.id)} sx={{ cursor: "help", display: "inline-block", ...(hasMultipleAiscGuid && { borderBottom: "1px dotted rgba(0, 0, 0, 0.3)" }) }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="body2" fontWeight="medium">{aiscGuid.value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>{aiscGuid.period}</Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover open={Boolean(aiscGuidanceAnchorEl[project.id])} anchorEl={aiscGuidanceAnchorEl[project.id]} onClose={() => handleAiscGuidancePopoverClose(project.id)} anchorOrigin={{ vertical: "center", horizontal: "left" }} transformOrigin={{ vertical: "center", horizontal: "right" }} disableRestoreFocus sx={{ pointerEvents: "none" }} PaperProps={{ sx: { pointerEvents: "auto", p: 2, maxWidth: 400 } }}>
                  {getCostTooltip(project.aisc, true, project.primaryMetal, "AISC Guidance")}
                </Popover>
              </TableCell>

              <TableCell align="right">
                {(() => {
                  const cashHist = formatCashCostHist(project)
                  if (!cashHist) return <Typography variant="body2">N/A</Typography>
                  const hasMultipleCashHist = (project.cashCost?.filter((c: {period: string}) => !c.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box component="span" onMouseEnter={(e) => handleCashCostHistPopoverOpen(e, project.id)} onMouseLeave={() => handleCashCostHistPopoverClose(project.id)} sx={{ cursor: "help", display: "inline-block", ...(hasMultipleCashHist && { borderBottom: "1px dotted rgba(0, 0, 0, 0.3)" }) }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="body2" fontWeight="medium">{cashHist.value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>{cashHist.period}</Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover open={Boolean(cashCostHistAnchorEl[project.id])} anchorEl={cashCostHistAnchorEl[project.id]} onClose={() => handleCashCostHistPopoverClose(project.id)} anchorOrigin={{ vertical: "center", horizontal: "left" }} transformOrigin={{ vertical: "center", horizontal: "right" }} disableRestoreFocus sx={{ pointerEvents: "none" }} PaperProps={{ sx: { pointerEvents: "auto", p: 2, maxWidth: 400 } }}>
                  {getCostTooltip(project.cashCost, false, project.primaryMetal, "Cash Cost Historical")}
                </Popover>
              </TableCell>

              <TableCell align="right">
                {(() => {
                  const cashGuid = formatCashCostGuidance(project)
                  if (!cashGuid) return <Typography variant="body2">N/A</Typography>
                  const hasMultipleCashGuid = (project.cashCost?.filter((c: {period: string}) => c.period.endsWith('E')) ?? []).length > 1
                  return (
                    <Box component="span" onMouseEnter={(e) => handleCashCostGuidancePopoverOpen(e, project.id)} onMouseLeave={() => handleCashCostGuidancePopoverClose(project.id)} sx={{ cursor: "help", display: "inline-block", ...(hasMultipleCashGuid && { borderBottom: "1px dotted rgba(0, 0, 0, 0.3)" }) }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="body2" fontWeight="medium">{cashGuid.value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>{cashGuid.period}</Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <Popover open={Boolean(cashCostGuidanceAnchorEl[project.id])} anchorEl={cashCostGuidanceAnchorEl[project.id]} onClose={() => handleCashCostGuidancePopoverClose(project.id)} anchorOrigin={{ vertical: "center", horizontal: "left" }} transformOrigin={{ vertical: "center", horizontal: "right" }} disableRestoreFocus sx={{ pointerEvents: "none" }} PaperProps={{ sx: { pointerEvents: "auto", p: 2, maxWidth: 400 } }}>
                  {getCostTooltip(project.cashCost, true, project.primaryMetal, "Cash Cost Guidance")}
                </Popover>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      </TableContainer>

      {/* Watchlist Modal */}
      {selectedProject && (
        <AddToWatchlistModal
          open={watchlistModalOpen}
          onClose={handleCloseWatchlistModal}
          projectId={selectedProject.id}
          projectName={selectedProject.name}
        />
      )}
    </Box>
  )
}
